import apiService from '../Api'
import {
  IEventCommissionPaymentResult,
  IEventCommissionSalePayment,
  IEventCommissionSalesFilter,
  IEventCommissionSalesResponse,
  IEventCommissionSummaryFilter,
  IEventCommissionSummaryResponse
} from './interfaces'

export default class EventCommissionService {
  async listSales(filter: IEventCommissionSalesFilter): Promise<IEventCommissionSalesResponse> {
    const params = new URLSearchParams()
    params.append('page', filter.page.toString())
    params.append('page_size', filter.page_size.toString())
    if (filter.search) params.append('search', filter.search)
    if (filter.event_id) params.append('event_id', filter.event_id.toString())
    if (filter.month) params.append('month', filter.month)
    if (filter.payment_status) params.append('payment_status', filter.payment_status)
    appendBeneficiary(params, filter)

    return apiService.get('/event-commissions', { params }).then((r) => r.data)
  }

  async listSummary(filter: IEventCommissionSummaryFilter): Promise<IEventCommissionSummaryResponse> {
    const params = new URLSearchParams()
    if (filter.event_id) params.append('event_id', filter.event_id.toString())
    if (filter.month) params.append('month', filter.month)
    if (filter.payment_status) params.append('payment_status', filter.payment_status)
    appendBeneficiary(params, filter)

    return apiService.get('/event-commissions/summary', { params }).then((r) => r.data)
  }

  /**
   * Dá baixa em todas as vendas em aberto do escopo — a linha inteira do beneficiário, já
   * recortada pelos filtros da listagem. Quem decide quais vendas entram é o backend, porque a
   * tela não tem os pedidos em mãos.
   */
  async markPaid(scope: IEventCommissionSummaryFilter, paidOn: string): Promise<IEventCommissionPaymentResult> {
    return apiService
      .post('/event-commissions/payments', { ...beneficiaryBody(scope), paid_on: paidOn })
      .then((r) => r.data)
  }

  async unmarkPaid(scope: IEventCommissionSummaryFilter): Promise<{ removed: number }> {
    const params = new URLSearchParams()
    if (scope.event_id) params.append('event_id', scope.event_id.toString())
    if (scope.month) params.append('month', scope.month)
    appendBeneficiary(params, scope)

    return apiService.delete('/event-commissions/payments', { params }).then((r) => r.data)
  }

  /** Também é o caminho de correção: repetir com outra data sobrescreve a anterior. */
  async markSalePaid(orderUuid: string, paidOn: string): Promise<IEventCommissionSalePayment> {
    return apiService.put(`/event-commissions/payments/${orderUuid}`, { paid_on: paidOn }).then((r) => r.data)
  }

  async unmarkSalePaid(orderUuid: string): Promise<{ removed: number }> {
    return apiService.delete(`/event-commissions/payments/${orderUuid}`).then((r) => r.data)
  }
}

/**
 * Escopo do pagamento, com as mesmas chaves que a query string dos GETs — menos `payment_status`,
 * que fica de fora de propósito: filtrar por situação não faz sentido num pedido de quitação, e o
 * backend já ignora venda que também já está paga.
 */
function beneficiaryBody(scope: IEventCommissionSummaryFilter): Record<string, string> {
  const body: Record<string, string> = {}
  if (scope.event_id) body.event_id = scope.event_id.toString()
  if (scope.month) body.month = scope.month
  if (scope.commission_type) body.commission_type = scope.commission_type
  if (scope.commission_target_id) body.commission_target_id = scope.commission_target_id.toString()
  if (scope.partner_name) body.partner_name = scope.partner_name

  return body
}

/** Os dois endpoints escopam ao beneficiário do mesmo jeito. */
function appendBeneficiary(params: URLSearchParams, filter: IEventCommissionSummaryFilter): void {
  if (filter.commission_type) params.append('commission_type', filter.commission_type)
  if (filter.commission_target_id) params.append('commission_target_id', filter.commission_target_id.toString())
  if (filter.partner_name) params.append('partner_name', filter.partner_name)
}
